import { useTranslation } from 'react-i18next';

import { DataPanelAutoWrapInlineItem, DataPanelItem } from '@/ui';
import type { Caller } from '@/signalr';
import { DateFormatter } from '@/features/shared/DateFormatter';

type GoogleElsDetections = NonNullable<Caller['dataSources']['googleEls'][number]['data']>['detections'];
type Detection = {
  kind: 'CarCrash' | 'Fall' | 'LossOfPulse';
  detectedAtUtc: string;
};

export type GoogleElsDetectionsPanelItemProps = {
  detections: GoogleElsDetections;
};

export function GoogleElsDetectionsPanelItem({ detections }: GoogleElsDetectionsPanelItemProps) {
  const { t } = useTranslation();
  const reportedDetections = [
    { kind: 'CarCrash' as const, detectedAtUtc: detections?.adrCarCrashAtUtc },
    { kind: 'Fall' as const, detectedAtUtc: detections?.fallDetectedAtUtc },
    { kind: 'LossOfPulse' as const, detectedAtUtc: detections?.lossOfPulseAtUtc },
  ]
    .filter((detection): detection is Detection => detection.detectedAtUtc != null)
    .sort(
      (left, right) => left.detectedAtUtc.localeCompare(right.detectedAtUtc) || left.kind.localeCompare(right.kind),
    );

  if (reportedDetections.length === 0) {
    return <DataPanelAutoWrapInlineItem title={t('Incident_InfoPanel_Detections')} showEmptyPlaceholder />;
  }

  return (
    <DataPanelItem title={t('Incident_InfoPanel_Detections')}>
      {reportedDetections.map((detection) => (
        <div
          key={`${detection.kind}-${detection.detectedAtUtc}`}
          className="d-flex flex-wrap justify-content-between gap-1"
        >
          <span>{t(`Incident_InfoPanel_Detection_${detection.kind}`)}</span>
          <DateFormatter date={detection.detectedAtUtc} formatType="calendar" />
        </div>
      ))}
    </DataPanelItem>
  );
}
